// Avatar — círculo com iniciais + tone color. Tone 1..8 mapeia pra tokens --tone-N-*.
// Com `src`, mostra a foto real (round, cover); ausente ou com erro de load cai de
// volta pras iniciais+tom. Mantém SRP: só apresentação, nada de fetch.
import { useEffect, useState, type HTMLAttributes } from 'react';
import styles from './Avatar.module.css';
import { cn } from '@/lib/utils/cn';
// As iniciais moram no util porque a regra não é tipográfica: contato sem
// profile name tem o TELEFONE no lugar do nome, e aí o que identifica é o DDD.
import { avatarInitials } from '@/lib/utils/formatters';

export type AvatarTone = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg';

export interface AvatarProps extends HTMLAttributes<HTMLSpanElement> {
  name: string;
  tone?: AvatarTone;
  size?: AvatarSize;
  /** URL da foto real. Ausente/erro de load → fallback pras iniciais+tom. */
  src?: string | null;
}

const toneClass: Record<AvatarTone, string> = {
  1: styles.tone1,
  2: styles.tone2,
  3: styles.tone3,
  4: styles.tone4,
  5: styles.tone5,
  6: styles.tone6,
  7: styles.tone7,
  8: styles.tone8,
};

const sizeClass: Record<AvatarSize, string> = {
  xs: styles.xs,
  sm: styles.sm,
  md: styles.md,
  lg: styles.lg,
};

export function Avatar({ name, tone = 1, size = 'md', src, className, ...rest }: AvatarProps) {
  // Reseta o estado de erro quando a URL muda (ex.: troca de foto reflete na hora).
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);

  const showImage = Boolean(src) && !failed;

  return (
    <span
      className={cn(styles.avatar, toneClass[tone], sizeClass[size], className)}
      aria-label={`Avatar de ${name}`}
      role="img"
      {...rest}
    >
      {showImage ? (
        // alt vazio de propósito: o <span role="img"> já carrega o aria-label.
        <img className={styles.image} src={src ?? undefined} alt="" onError={() => setFailed(true)} />
      ) : (
        avatarInitials(name)
      )}
    </span>
  );
}
